import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../store/rootReducer';
import api, {useGetSubOrganizationsQuery} from '../store/Api';
import {useGetOrganizationEntitlementsQuery} from '../store/endpoints/OrganizationsEndpoints';
import {ISubOrganization} from '../models/Settings';
import {setSelectedSubOrganization} from '../store/settingsSlice';

export interface ISubOrganizationScope {
    /** Id of the selected sub-organization, or undefined when the whole organization is in scope. */
    selectedId?: string;
    /** The selected sub-organization record, once the list has loaded. */
    selected?: ISubOrganization;
    /** Friendly name for the selected sub-organization, falling back to the id. */
    selectedName?: string;
    /** Every sub-organization under the selected organization. */
    subOrganizations: ISubOrganization[];
    /** True when the tenant is entitled to sub-organizations and has at least one. */
    isAvailable: boolean;
    /** True while a sub-organization is selected, so the whole app is customer scoped. */
    isScoped: boolean;
    /** Resolve any sub-organization id to its name, falling back to the id itself. */
    nameOf: (id?: string) => string | undefined;
}

/**
 * Everything a screen needs to react to the global sub-organization (customer) scope.
 *
 * The list query is shared through RTK Query's cache, so calling this from several components costs
 * one request. The entitlement gate mirrors the settings navigation: `subOrganizationsEnabled` plus
 * at least one sub-organization actually existing.
 */
export const useSubOrganizationScope = (): ISubOrganizationScope => {
    const selectedOrganization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const selectedId = useSelector((state: RootState) => state.settings.selectedSubOrganization);
    const entitlements = useGetOrganizationEntitlementsQuery(selectedOrganization ?? '', {skip: !selectedOrganization});
    const enabled = entitlements.data?.subOrganizationsEnabled === true;
    const subOrganizations = useGetSubOrganizationsQuery(undefined, {skip: !enabled});

    const items = React.useMemo(() => subOrganizations.data ?? [], [subOrganizations.data]);
    const selected = React.useMemo(() => items.find(s => s.id === selectedId), [items, selectedId]);
    const nameOf = React.useCallback(
        (id?: string) => (id ? items.find(s => s.id === id)?.name ?? id : undefined),
        [items]
    );

    return {
        selectedId,
        selected,
        selectedName: selectedId ? selected?.name ?? selectedId : undefined,
        subOrganizations: items,
        isAvailable: enabled && items.length > 0,
        isScoped: !!selectedId,
        nameOf,
    };
};

/**
 * Switch the sub-organization scope and drop every cached response gathered under the old one.
 *
 * RTK Query keys its cache on endpoint arguments, not on request headers, so a query with identical
 * arguments would happily serve the previous customer's pricing (or the parent organization's) after
 * a switch. Resetting API state is the same hammer `useSwitchOrganization` uses for `x-organization`.
 */
export const useSwitchSubOrganization = (): ((subOrganization?: string) => void) => {
    const dispatch = useDispatch();
    return React.useCallback(
        (subOrganization?: string) => {
            dispatch(setSelectedSubOrganization(subOrganization));
            dispatch(api.util.resetApiState());
        },
        [dispatch]
    );
};

export default useSubOrganizationScope;
